import { useCallback, useContext, useMemo } from "react";
import { useStyles } from "../RCACompliance.styles";
import { IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ComplianceBar from "pages/Corporate/RCA/RCAAPCompliance/components/ComplianceBar";
import { ComplianceBarReferences } from "pages/Corporate/RCA/RCAAPCompliance/components/ComplianceBar/ComplianceBarReferences.view";
import { ComplianceBarPercentageReference } from "pages/Corporate/RCA/RCAAPCompliance/components/ComplianceBar/ComplianceBarPercentageReference.view";
import { RCACorporateDashboardContext } from "contexts/RCACorporateDashboardContext/RCACorporateDashboardContext";
import moment from "moment";
import { RCACorporateDashboardEntity } from "entities/RCA/RCACorporateDashboardEntity/RCACorporateDashboardEntity";
import { IRCAComplianceByMillCorporate } from "entities/RCA/RCACorporateDashboardEntity/RCACorporateDashboardEntityInterfaces";
import {
    IRCAAPBarComplianceTypes,
    RCAAPComplianceByResponsibleFilterEnum,
} from "pages/Corporate/RCA/RCAAPCompliance/RCAAPComplianceInterfaces";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ComplianceScore, ComplianceTypes } from "constants/constants";
import RCAAPComplianceByResponsibleFilter from "pages/Corporate/RCA/RCAAPCompliance/components/filters/RCAAPComplianceByResponsibleFilter/RCAAPComplianceByResponsibleFilter.view";

const computeMillScore = (row: IRCAComplianceByMillCorporate) =>
    row.completed_on_time * ComplianceScore.COMPLETED_ON_TIME +
    row.pending * ComplianceScore.PENDING +
    row.completed_late * ComplianceScore.COMPLETED_LATE +
    row.pending_late * ComplianceScore.PENDING_LATE;

const applyComplianceTypeFilter = (data: IRCAComplianceByMillCorporate[], selected: string) => {
    if (selected === ComplianceTypes.NONE) return data;
    return data.map((row) => ({
        ...row,
        completed_on_time: selected === "completed_on_time" ? row.completed_on_time : 0,
        pending: selected === "pending" ? row.pending : 0,
        completed_late: selected === "completed_late" ? row.completed_late : 0,
        pending_late: selected === "pending_late" ? row.pending_late : 0,
    }));
};

const RCAComplianceByMill = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters, handleSetTypeFilter, handleSetComplianceType } = useContext(RCACorporateDashboardContext);
    const argsFilters = useMemo(
        () => ({
            date: moment().subtract(Number(filters.periodOption), "day").startOf("day").toISOString(),
            divisionIds: filters.divisionIds,
            totalDivisions: filters.totalDivisions,
            regionIds: filters.regionIds,
            totalRegions: filters.totalRegions,
        }),
        [filters],
    );
    const { rcaComplianceByMill } = RCACorporateDashboardEntity.GetRCAComplianceByMill(argsFilters);

    const filteredData = applyComplianceTypeFilter(rcaComplianceByMill, filters.complianceType);

    const sortMills = useCallback((data: IRCAComplianceByMillCorporate[], criteria: string) => {
        const millName = (row: IRCAComplianceByMillCorporate) => row.mill?.name ?? "";
        switch (criteria) {
            case RCAAPComplianceByResponsibleFilterEnum.a_to_z_asc:
                return [...data].sort((a, b) => millName(a).localeCompare(millName(b)));
            case RCAAPComplianceByResponsibleFilterEnum.a_to_z_desc:
                return [...data].sort((a, b) => millName(b).localeCompare(millName(a)));
            case RCAAPComplianceByResponsibleFilterEnum.low_to_high:
                return [...data].sort((a, b) => computeMillScore(a) - computeMillScore(b));
            case RCAAPComplianceByResponsibleFilterEnum.high_to_low:
                return [...data].sort((a, b) => computeMillScore(b) - computeMillScore(a));
            default:
                return [...data].sort((a, b) => millName(a).localeCompare(millName(b)));
        }
    }, []);

    const barsData = useMemo(() => {
        const sorted = sortMills(filteredData, filters.typeOption);
        return sorted.map((row) => {
            const totalWhenComplianceTypeSelected =
                filters.complianceType !== ComplianceTypes.NONE
                    ? row.completed_on_time + row.pending + row.completed_late + row.pending_late
                    : row.total_rcas_by_period;
            return [
                {
                    responsible: {
                        name: row.mill?.name ?? "",
                        id: row.mill_id,
                        hideAvatar: true,
                    },
                    completedOnTimeAmount: row.completed_on_time,
                    pendingAmount: row.pending,
                    completedLateAmount: row.completed_late,
                    pendingLateAmount: row.pending_late,
                    total: totalWhenComplianceTypeSelected,
                },
            ];
        });
    }, [filteredData, filters, sortMills]);

    return (
        <div className={classes.card}>
            <div className={classes.cardHeader}>
                <div className={classes.cartTitleContainer}>
                    <Typography variant="rcaTitle" className={classes.cardTitle}>
                        {t("rca.corporate_dashboard.rca_compliance.by_mill.title")}
                    </Typography>
                    <IconButton onClick={() => handleSetComplianceType(ComplianceTypes.NONE)}>
                        <RefreshIcon />
                    </IconButton>
                </div>
                <RCAAPComplianceByResponsibleFilter filters={filters} setFilters={handleSetTypeFilter} />
            </div>
            <div className={classes.cardBody}>
                {barsData.length === 0 ? (
                    <ComplianceBar />
                ) : (
                    <>
                        <ComplianceBarPercentageReference />
                        {barsData
                            .filter((barData) =>
                                Object.keys(barData[0]).some(
                                    (key) =>
                                        (Object.values(IRCAAPBarComplianceTypes) as string[]).includes(key) &&
                                        barData[0][key as IRCAAPBarComplianceTypes] > 0,
                                ),
                            )
                            .map((barData, index) => (
                                <ComplianceBar
                                    hidePercentage={filters.complianceType !== ComplianceTypes.NONE}
                                    data={barData}
                                    key={index}
                                    withBottomBorder={true}
                                    onComplianceTypeSelected={handleSetComplianceType}
                                />
                            ))}
                        {barsData.length > 0 && <ComplianceBarReferences />}
                    </>
                )}
            </div>
        </div>
    );
};

export default RCAComplianceByMill;
